import FuxEvents from "../lib/FuxFramework/FuxEvents";
import {
    NM_EXTERNAL_REQUEST_CANCELLED,
    NM_EXTERNAL_REQUEST_LAUNCH_FAILED,
    NM_EXTERNAL_REQUEST_RESULT
} from "./NativeMessageHandler";

export const EXTERNAL_REQUEST_MODE_DEEP_LINK = 'deepLink';
export const EXTERNAL_REQUEST_MODE_ACTIVITY_RESULT = 'activityResult';
export const EXTERNAL_REQUEST_CALLBACK_BASE_URL = 'https://bookizon.it/_dl/';

const DEFAULT_TIMEOUT_MS = 180000;
const MIN_TIMEOUT_MS = 5000;
const MAX_TIMEOUT_MS = 900000;
const REQUEST_ID_PATTERN = /^[A-Za-z0-9._-]{1,128}$/;

const createRequestId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }

    return 'external-' + Date.now() + '-' + Math.random().toString(36).substring(2, 12);
};

const validateRequestId = requestId => {
    if (typeof requestId !== 'string' || !REQUEST_ID_PATTERN.test(requestId)) {
        throw new Error('External requestId non valido');
    }
};

const validateTimeout = timeoutMs => {
    if (!Number.isInteger(timeoutMs) || timeoutMs < MIN_TIMEOUT_MS || timeoutMs > MAX_TIMEOUT_MS) {
        throw new Error('External request timeoutMs non valido');
    }
};

export const createExternalRequestContext = requestId => {
    const resolvedRequestId = requestId || createRequestId();
    validateRequestId(resolvedRequestId);

    return {
        requestId: resolvedRequestId,
        callbackUri: EXTERNAL_REQUEST_CALLBACK_BASE_URL + encodeURIComponent(resolvedRequestId)
    };
};

const removeListeners = listeners => {
    listeners.forEach(listener => FuxEvents.off(listener.eventName, listener.handler));
};

const createRequestError = message => {
    const error = new Error(message.message || 'Richiesta esterna non riuscita');
    error.code = message.code || 'EXTERNAL_REQUEST_FAILED';
    error.requestId = message.requestId;
    error.details = message;
    return error;
};

export const launchExternalRequest = options => {
    const request = options || {};
    const requestId = request.requestId;
    const targetUri = request.targetUri;
    const mode = request.mode || EXTERNAL_REQUEST_MODE_DEEP_LINK;
    const timeoutMs = request.timeoutMs === undefined ? DEFAULT_TIMEOUT_MS : request.timeoutMs;

    validateRequestId(requestId);
    validateTimeout(timeoutMs);

    if (typeof targetUri !== 'string' || targetUri.length === 0) {
        return Promise.reject(new Error('External request targetUri non valido'));
    }

    if ([EXTERNAL_REQUEST_MODE_DEEP_LINK, EXTERNAL_REQUEST_MODE_ACTIVITY_RESULT].indexOf(mode) === -1) {
        return Promise.reject(new Error('External request mode non supportato'));
    }

    if (!window.AndroidInterface || typeof window.AndroidInterface.launchExternalRequest !== 'function') {
        return Promise.reject(new Error('External request disponibile solo nell’app Android Bookizon'));
    }

    return new Promise((resolve, reject) => {
        let timeout = null;
        const listeners = [];

        const finish = callback => message => {
            if (!message || message.requestId !== requestId) {
                return;
            }

            if (timeout !== null) {
                clearTimeout(timeout);
            }
            removeListeners(listeners);
            callback(message);
        };

        listeners.push({
            eventName: NM_EXTERNAL_REQUEST_RESULT,
            handler: finish(resolve)
        });
        listeners.push({
            eventName: NM_EXTERNAL_REQUEST_LAUNCH_FAILED,
            handler: finish(message => reject(createRequestError(message)))
        });
        listeners.push({
            eventName: NM_EXTERNAL_REQUEST_CANCELLED,
            handler: finish(message => reject(createRequestError(message)))
        });
        listeners.forEach(listener => FuxEvents.on(listener.eventName, listener.handler));

        timeout = setTimeout(() => {
            removeListeners(listeners);
            if (typeof window.AndroidInterface.cancelExternalRequest === 'function') {
                window.AndroidInterface.cancelExternalRequest(requestId);
            }
            const error = new Error('Timeout della richiesta esterna');
            error.code = 'EXTERNAL_REQUEST_TIMEOUT';
            error.requestId = requestId;
            reject(error);
        }, timeoutMs);

        window.AndroidInterface.launchExternalRequest(JSON.stringify({
            requestId: requestId,
            targetUri: targetUri,
            mode: mode,
            timeoutMs: timeoutMs
        }));
    });
};

const ExternalRequestUtils = {
    createContext: createExternalRequestContext,
    launch: launchExternalRequest
};

export default ExternalRequestUtils;
